import { useCallback, useMemo } from 'react';
import { type Vendor } from '@sg/graphql-schema';
import {
  useCustomizationContext,
  type useInitCustomizationMachine,
} from '@sg/shared-fe/features/customization';

import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

import {
  useAddLineItemToCart,
  useCart,
  useEditLineItemInCart,
  useRemoveLineItemFromCart,
} from '../../../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const useCustomizeCartMutations = (params: Params) => {
  const {
    actor,
    addressId,
    deliveryFee,
    vendor,
    lineItemId,
    onAddLineItemToCart,
    onEditLineItemInCart,
    onRemoveLineItemFromCart,
  } = params;

  const {
    product,
    quantity,
    restaurant,
    modifications,
    mixedDressingDetails,
    totalCost,
  } = useCustomizationContext({ actor });

  const { cart, cartOrderType, total, orderId } = useCart();
  const { track } = useTelemetry();

  const addLineItemToCartMutation = useAddLineItemToCart({ onSuccess: onAddLineItemToCart }); // prettier-ignore
  const editLineItemInCartMutation = useEditLineItemInCart({ onSuccess: onEditLineItemInCart }); // prettier-ignore
  const removeFromCartMutation = useRemoveLineItemFromCart({ onSuccess: onRemoveLineItemFromCart }); // prettier-ignore

  const { addLineItemToCart, fetching: isAddingLineItemToCart } = addLineItemToCartMutation; // prettier-ignore
  const { editLineItemInCart, fetching: isEditingLineItemInCart } = editLineItemInCartMutation; // prettier-ignore
  const { removeLineItemFromCart, fetching: isRemovingLineItemFromCart } = removeFromCartMutation; // prettier-ignore

  // ─── Derived Data ────────────────────────────────────────────────────────────

  const deliveryDetails = useMemo(() => {
    if (!addressId) return;

    return {
      tip: 0,
      addressId,
      vendor: vendor?.name ?? '',
      vendorRestaurantId: vendor?.restaurantId ?? '',
      deliveryFee: deliveryFee ?? 0,
    };
  }, [vendor, addressId, deliveryFee]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const onAddLineItemToCartTelemetry = useCallback(() => {
    track('pdp.add_to_bag', {
      orderId,
      restaurantId: restaurant?.id,
      restaurantName: restaurant?.name,
      orderValue: total,
      orderType: cartOrderType,
      bagCount: cart?.lineItems?.length,
      selectedProduct: telemetryProductFromInput(
        { ...product, quantity, location: restaurant?.slug },
        modifications,
      ),
    });
  }, [
    cart?.lineItems?.length,
    cartOrderType,
    modifications,
    orderId,
    product,
    quantity,
    restaurant?.id,
    restaurant?.name,
    restaurant?.slug,
    total,
    track,
  ]);

  const modifyCart = useCallback(async () => {
    if (!product) return;

    onAddLineItemToCartTelemetry();

    const modificationsInput = {
      additions: modifications?.additions ?? [],
      removals: modifications?.removals ?? [],
      substitutions: modifications?.substitutions ?? [],
      mixedDressingDetails: mixedDressingDetails ?? [],
    };

    // ─── Edit Line Item ──────────────────────────────────────────

    if (lineItemId) {
      await editLineItemInCart({
        product,
        lineItemId,
        quantity,
        modifications: modificationsInput,
      });

      return;
    }

    // ─── Add Line Item ────────────────────────────────────────────

    await addLineItemToCart({
      product,
      quantity,
      deliveryDetails,
      totalCost,
      modifications: modificationsInput,
    });
  }, [
    product,
    onAddLineItemToCartTelemetry,
    lineItemId,
    addLineItemToCart,
    quantity,
    deliveryDetails,
    totalCost,
    modifications?.additions,
    modifications?.removals,
    modifications?.substitutions,
    mixedDressingDetails,
    editLineItemInCart,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    removeLineItemFromCart,
    modifyCart,
    isAddingLineItemToCart,
    isEditingLineItemInCart,
    isRemovingLineItemFromCart,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  actor: ReturnType<typeof useInitCustomizationMachine>;
  addressId?: string;
  deliveryFee?: number;
  vendor?: Vendor;
  lineItemId?: string;
  onAddLineItemToCart?: () => void;
  onEditLineItemInCart?: () => void;
  onRemoveLineItemFromCart?: () => void;
};
